import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import type { TenantCode, TenantTheme } from './types';
import { sflTheme } from './tenants/sfl';
import { xichengTheme } from './tenants/xicheng';
import { buildAntdTheme } from './antdTheme';
import { fetchTenantTheme } from '@/api/tenants';

const builtinThemes: Record<string, TenantTheme> = {
  sfl: sflTheme,
  xicheng: xichengTheme,
};

/**
 * Sprint15 简化:删 selected vs current 双 tenant 分离(原 Sprint8 Group F)。
 *
 * 历史:Sprint8 引入"跨租户视角"机制 — selectedTenant ≠ currentTenant 允许 platform_operator
 * 切到其他租户的视角看数据。但该设计有两大问题:
 *   1. 比 impersonate 更隐蔽 — 切视角无 audit_log,改密不失效,无确认弹窗
 *   2. "写仍走 home" 是口头承诺,后端无强制(实际后端零消费 X-Tenant-Code)
 * 严格分工后跨租户读走聚合仪表盘 + 审计日志,故 Sprint15 删此机制。
 *
 * 现状:
 *   - tenant     当前白标主题(localStorage.tenant_code,登录时按 host 解析)
 *   - selectedTenant / currentTenant / effectiveTenant  全部 = tenant(向后兼容别名)
 *   - setTenantCode 仅切白标主题(改 CSS variables + dispatch tenant-code-changed 事件),
 *                    不再改变 API 行为,不再发"跨租户数据视角"语义
 */
interface TenantContextValue {
  /** 当前白标主题 */
  tenant: TenantTheme;
  /** @deprecated 用 {@link tenant};Sprint15 已无双 tenant 概念,此别名等同 tenant */
  selectedTenant: TenantTheme;
  /** @deprecated 用 {@link tenant};Sprint15 已无双 tenant 概念,此别名等同 tenant */
  currentTenant: TenantTheme;
  /** 切换白标主题(仅 UI 颜色/logo;不影响 API 行为) */
  setTenantCode: (code: TenantCode) => void;
  /** @deprecated 用 {@link tenant.code} */
  effectiveCode: TenantCode;
  /** @deprecated 用 {@link tenant} */
  effectiveTenant: TenantTheme;
  /** @deprecated 用 {@link tenant.code} */
  getEffectiveTenantCode: () => TenantCode;
  /** @deprecated 用 {@link tenant} */
  getEffectiveTenant: () => TenantTheme;
  availableTenants: TenantTheme[];
}

const TenantContext = createContext<TenantContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be called inside <TenantThemeProvider>');
  }
  return ctx;
}

interface Props {
  /** 初始租户 code(不传则读 localStorage,回退 sfl) */
  defaultCode?: TenantCode;
  children: ReactNode;
}

export function TenantThemeProvider({ defaultCode, children }: Props) {
  const initialCode: TenantCode =
    defaultCode ?? (localStorage.getItem('tenant_code') as TenantCode) ?? 'sfl';

  // Sprint15:单 tenant 状态 — 双 tenant 概念已删除(见文件顶部注释)。
  const [tenant, setTenant] = useState<TenantTheme>(
    builtinThemes[initialCode] ?? sflTheme,
  );

  const setTenantCode = (code: TenantCode) => {
    const next = builtinThemes[code];
    if (next) {
      setTenant(next);
      localStorage.setItem('tenant_code', code);
      // 通知 BrandingContext 同步 refetch tenant_branding
      window.dispatchEvent(new Event('tenant-code-changed'));
    } else {
      // 未在 builtin 里 — 走 mock API 拉(给未来真后端 tenant list 留接口)
      fetchTenantTheme(code)
        .then((remote) => {
          setTenant(remote);
          localStorage.setItem('tenant_code', code);
          window.dispatchEvent(new Event('tenant-code-changed'));
        })
        .catch(() => {
          // fallback:保持当前;后续可上 message.warn
        });
    }
  };

  // 同步 data-theme 到 <html>,CSS variables 生效
  useEffect(() => {
    document.documentElement.dataset.theme = tenant.code;
  }, [tenant.code]);

  const antdConfig = useMemo(() => buildAntdTheme(tenant), [tenant]);

  // 向后兼容别名 getter(标 @deprecated)— 旧调用点不破,新代码直接读 tenant
  const getEffectiveTenantCode = (): TenantCode => tenant.code as TenantCode;
  const getEffectiveTenant = (): TenantTheme => tenant;

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      // 向后兼容:旧调用点用 selectedTenant/currentTenant/effective* 全部等同 tenant
      selectedTenant: tenant,
      currentTenant: tenant,
      setTenantCode,
      effectiveCode: tenant.code as TenantCode,
      effectiveTenant: tenant,
      getEffectiveTenantCode,
      getEffectiveTenant,
      availableTenants: Object.values(builtinThemes),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenant],
  );

  return (
    <TenantContext.Provider value={value}>
      <ConfigProvider locale={zhCN} theme={antdConfig}>
        {children}
      </ConfigProvider>
    </TenantContext.Provider>
  );
}
